import { IntegrationNames } from '@/features/integrations';
import type { WarehouseSyncCreateInput } from '@/services/db/warehouse-sync/mutations';
import type { WarehouseDestination } from '@/services/db/warehouse-sync/entity';

import s3Asset from '@/assets/images/integrations/s3.svg';
import googleCloudStorageAsset from '@/assets/images/integrations/google-cloud-storage.svg';
import yandexAsset from '@/assets/images/integrations/yandex.svg';
import azureBlobStorageAsset from '@/assets/images/integrations/azure-blob-storage.svg';

/** Every field any provider's bucket config can carry, each optional. */
type AllBucketConfigFields = NonNullable<WarehouseSyncCreateInput['s3_config']> &
  NonNullable<WarehouseSyncCreateInput['gcs_config']> &
  NonNullable<WarehouseSyncCreateInput['yandex_config']> &
  NonNullable<WarehouseSyncCreateInput['azure_config']>;

/**
 * A bucket config under construction. Derived from the generated request types, so
 * a form emitting the wrong field name (`secretKey` for `secret_key`) fails to
 * compile rather than silently posting a config the server reads as blank.
 */
export type BucketConfigDraft = Partial<AllBucketConfigFields>;

export type BucketConfigField = keyof AllBucketConfigFields;

/**
 * Display metadata for the four storage destinations a warehouse sync can write
 * to. The destination union itself lives with the entity, since it is derived
 * from which `*_config` block the record carries.
 */
type WarehouseDestinationInfo = {
  name: string;
  imageSrc: string;
  documentationUrl: string;
};

const DESTINATION_INFO: Readonly<Record<WarehouseDestination, WarehouseDestinationInfo>> = Object.freeze({
  s3: {
    name: IntegrationNames.amazonS3,
    imageSrc: s3Asset,
    documentationUrl: 'https://docs.customer.io/amazon-s3-data-out',
  },
  gcs: {
    name: IntegrationNames.gcs,
    imageSrc: googleCloudStorageAsset,
    documentationUrl: 'https://docs.customer.io/gcs-data-out',
  },
  // Not `IntegrationNames.yandex` ("Yandex"): the warehouse surface names the
  // cloud product rather than the company.
  yandex: {
    name: 'Yandex Cloud',
    imageSrc: yandexAsset,
    documentationUrl: 'https://docs.customer.io/yandex-data-out',
  },
  azure_blob: {
    name: IntegrationNames.azureBlob,
    imageSrc: azureBlobStorageAsset,
    documentationUrl: 'https://docs.customer.io/ms-azure-data-out',
  },
});

export function getWarehouseDestinationInfo(destination: WarehouseDestination): WarehouseDestinationInfo {
  return DESTINATION_INFO[destination];
}

export function getWarehouseDestinationName(destination: WarehouseDestination): string {
  return DESTINATION_INFO[destination].name;
}

export function getWarehouseDestinationImageSrc(destination: WarehouseDestination): string {
  return DESTINATION_INFO[destination].imageSrc;
}

export function getWarehouseDocumentationUrl(destination: WarehouseDestination): string {
  return DESTINATION_INFO[destination].documentationUrl;
}

/**
 * The credential fields each destination requires before its connection can be
 * validated. `bucket_path` is deliberately absent everywhere — it is optional
 * for every provider.
 */
export const WAREHOUSE_DESTINATION_REQUIRED_FIELDS: Readonly<
  Record<WarehouseDestination, readonly BucketConfigField[]>
> = Object.freeze({
  s3: ['access_key', 'secret_key', 'bucket_name', 'region'],
  gcs: ['bucket_name', 'credentials'],
  yandex: ['access_key', 'secret_key', 'bucket_name', 'region'],
  azure_blob: ['container_sas_url'],
});
